import React, { useEffect, useState } from 'react';
import insuranceDetailsService from '../insuranceDetails/insuranceDetailsService.js';
import PatientInfoService from '../patientInfo/patientInfoService.js';
import InsurancePatientInfoService from '../patientInfo/InsurancePatientInfoService.js';
import authorizationService from '../Authorization/authorizationService.js';
import PointsBankAPI from '../points/PointsBankAPI.js';
import DiagnosisCombinerService from '../Authorization/diagnosisCombinerService.js';
import PatientContactPermissionService from '../patientInfo/PatientContactPermissionService.js';

import PatientDetails from './PatientBoard/PatientDetails.jsx';
import UserPoints from './PatientBoard/UserPoints.jsx';
import InsuranceSection from './PatientBoard/InsuranceSection.jsx';
import AuthorizationSection from './PatientBoard/AuthorizationSection.jsx';

import './ptDash.css';

const PatientDashboard = () => {
  const [patient, setPatient] = useState(null);
  const [editedPatient, setEditedPatient] = useState({});
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userPoints, setUserPoints] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionId, setPermissionId] = useState(null);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const patientId = localStorage.getItem("patientId");

    const ptService = new PatientInfoService();
    const insService = new insuranceDetailsService();
    const insdetails = new InsurancePatientInfoService();
    const authService = new authorizationService();
    const pointService = new PointsBankAPI();
    const diagComb = new DiagnosisCombinerService();
    const permissionService = new PatientContactPermissionService();
    let interval;

    const fetchData = async () => {
      if (!patientId) {
        setError("No patient ID found in local storage.");
        return;
      }

      try {
        const patient = await ptService.getPatientById(patientId);
        if (isMounted) {
          setPatient(patient);
          setEditedPatient(patient);
          setUsername(patient.username || '');
        }

        const points = await pointService.getTotalPoints(patientId);
        if (isMounted) setUserPoints(points.points ?? 0);

        const insuranceRaw = await insdetails.getByPatientId(patientId);
        const insuranceCache = {};
        for (const entry of insuranceRaw) {
          const insId = entry.insId;
          if (!insuranceCache[insId]) {
            insuranceCache[insId] = await insService.getInsuranceNameById(insId);
          }
          entry.insuranceName = insuranceCache[insId];
        }
        if (isMounted) setInsuranceData(insuranceRaw);

        const authList = await authService.findByPatientId(patientId);
        for (const auth of authList) {
          const diag = await diagComb.getDiagnosisIdByAuthId(auth.authId);
          Object.assign(auth, diag);
        }
        if (isMounted) setAuthorizations(authList);

        const perms = await permissionService.getByPatientId(patientId);
        if (isMounted && perms) {
          setPermissions(perms);
          setPermissionId(perms.permissionId);
        }

        interval = setInterval(async () => {
          const updated = await pointService.getTotalPoints(patientId);
          if (isMounted) setUserPoints(updated.points ?? 0);
        }, 30000);
      } catch (e) {
        if (isMounted) {
          setError(`Failed to load data: ${e.message}`);
          console.error(e);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedPatient(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleSaveDetails = async () => {
    try {
      const ptService = new PatientInfoService();
      await ptService.savePatientDetails(patient.patientId, null, editedPatient);
      setPatient(editedPatient);
      showSuccess('Details updated successfully!');
    } catch (err) {
      console.error(err);
      alert(`Error saving details: ${err.message}`);
    }
  };

  const handleUpdateAccount = async () => {
    try {
      const ptService = new PatientInfoService();
      await ptService.updatePatientAccount(patient.patientId, username, password, editedPatient.email);
      showSuccess('Account updated successfully!');
    } catch (err) {
      console.error(err);
      alert(`Error updating account: ${err.message}`);
    }
  };

  const handleSavePreferences = async () => {
    if (!permissionId) {
      alert("Permission ID missing.");
      return;
    }

    try {
      const service = new PatientContactPermissionService();
      await service.update(permissionId, permissions);
      showSuccess("Preferences updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update preferences: " + err.message);
    }
  };

  if (error) return <div className="dashboard-container"><p className="error">{error}</p></div>;
  if (!patient) return <div className="dashboard-container"><p>Loading...</p></div>;

  return (
    <div className="dashboard-container">
      <h1 className="title">Patient Dashboard</h1>

      {successMessage && (
        <div className="success-message">
          ✅ {successMessage}
        </div>
      )}

      <div className="dashboard-grid">
        <div className="info-box">
          {editMode ? (
            <>
              <input type="text" name="firstName" value={editedPatient.firstName || ''} onChange={handleInputChange} placeholder="First Name" className="edit-input" />
              <input type="text" name="lastName" value={editedPatient.lastName || ''} onChange={handleInputChange} placeholder="Last Name" className="edit-input" />
              <input type="text" name="phone" value={editedPatient.phone || ''} onChange={handleInputChange} placeholder="Phone" className="edit-input" />
              <input type="email" name="email" value={editedPatient.email || ''} onChange={handleInputChange} placeholder="Email" className="edit-input" />
              <input type="text" name="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" className="edit-input" />
              <input type="password" name="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New Password" className="edit-input" />
              <button onClick={handleSaveDetails} className="save-button">Update Details</button>
              <button onClick={handleUpdateAccount} className="password-button">Update Account</button>
              <button onClick={handleEditToggle} className="cancel-button">Cancel</button>
            </>
          ) : (
            <>
              <PatientDetails patient={patient} />
              <button onClick={handleEditToggle} className="edit-button">Edit Profile</button>
            </>
          )}
        </div>

        <div className="info-box">
          <UserPoints points={userPoints} />
        </div>

        <div className="info-box">
          <InsuranceSection insuranceData={insuranceData} />
        </div>

        <div className="info-box">
          <AuthorizationSection authorizations={authorizations} />
        </div>

       <div className="info-box communication-box">
  <div className="toggle-header" onClick={() => setShowConsent(prev => !prev)}>
    <h3>📬 Communication Preferences</h3>
    <span>{showConsent ? "▲" : "▼"}</span>
  </div>

  {showConsent && (
    <div className="consent-body">
      {[
        {
          title: "Promotions/Messages",
          ids: ["allowPromotionText", "allowPromotionEmail"],
          description: `By checking these boxes, you agree to receive promotional content and messages via SMS or email.`
        },
        {
          title: "Appointment Reminders",
          ids: ["allowAppointmentReminderText", "allowAppointmentReminderEmail"],
          description: `Reminders will be sent the day of your appointment.`
        },
        {
          title: "Authorization Notifications",
          ids: ["allowAuthorizationReminderText", "allowAuthorizationReminderEmail"],
          description: `We’ll notify you before insurance authorization expires or runs out.`
        },
      ].map(({ title, ids, description }) => (
        <div key={title} className="checkbox-row">
          <p><strong>{title}</strong></p>
          <small>{description}</small>
          <div className="checkbox-group">
            {ids.map((id) => (
              <label key={id} className="checkbox-column">
                <input
                  type="checkbox"
                  checked={!!permissions[id]}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setPermissions(prev => ({ ...prev, [id]: checked }));
                  }}
                />
                {id.includes("Text") ? `${title} via text` : `${title} via email`}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button onClick={handleSavePreferences} className="save-button">Save Preferences</button>
    </div>
  )}
</div>

      </div>
    </div>
  );
};

export default PatientDashboard;
